import { useEffect } from 'react';

import { useThree } from '@react-three/fiber';
import * as THREE from 'three';

import { CameraControls, centerCameraOnBox } from '../../utils/cameraUtils.ts';
import { ModelFile, ModelLayer } from '../model-context/ModelContext.ts';
import { LayerSetting } from '../project-settings/types.ts';
import { InteractiveObject } from './InteractiveObject.tsx';
import { ModelLoader } from './ModelLoader.tsx';
import { ModelPreviewLighting } from './ModelPreviewLighting.tsx';

interface SceneProps {
  onObjectHover: (name: string | null) => void;
  onObjectClick: (name: string) => void;
  onObjectSelect?: (name: string) => void;
  hoveredLayer: string | null;
  selectedLayer?: string | null;
  layerSettings: LayerSetting[];
  hoveredLayerColor: string | null;
  uploadedModels?: ModelFile[] | null;
  onLayersFound?: (modelName: string, layers: ModelLayer[]) => void;
  visibleLayers?: string[];
  loadedModels?: Record<string, boolean>;
  /** Uniform scale applied to uploaded models (real-world preview). Defaults to 1. */
  modelScale?: number;
}

THREE.Object3D.DEFAULT_UP.set(0, 0, 1);

type ObjectType = {
  geometry: 'box' | 'sphere' | 'cylinder';
  position: [number, number, number];
  name: string;
  color: string;
};

export function ModelPreviewContent({
  onObjectHover,
  onObjectClick,
  onObjectSelect,
  hoveredLayer,
  selectedLayer,
  layerSettings,
  uploadedModels,
  onLayersFound,
  visibleLayers,
  loadedModels,
  modelScale = 1,
}: SceneProps) {
  const { camera, scene, controls } = useThree((state) => ({
    camera: state.camera,
    scene: state.scene,
    controls: state.controls as unknown as CameraControls,
  }));
  // Effect to center the camera on the models when they load and whenever
  // modelScale changes. We accumulate world-space bounds from the live scene
  // meshes (NOT clones) so parent transforms like <group scale={modelScale}>
  // are respected — cloning would detach the meshes from their scaled parent
  // and we'd compute bounds at the original size.
  useEffect(() => {
    if (!uploadedModels || uploadedModels.length === 0 || !scene) return;

    // Ensure world matrices reflect the latest React-applied transforms
    // (including the modelScale group) before we measure bounds.
    scene.updateMatrixWorld(true);

    const aggregate = new THREE.Box3();
    let hasMeshes = false;
    scene.traverse((object) => {
      if (
        object instanceof THREE.Mesh &&
        !object.userData.isHelper &&
        !object.userData.isGrid &&
        object.name !== 'CustomGrid' &&
        object.visible
      ) {
        const meshBox = new THREE.Box3().setFromObject(object);
        if (!meshBox.isEmpty()) {
          aggregate.union(meshBox);
          hasMeshes = true;
        }
      }
    });

    if (!hasMeshes || aggregate.isEmpty()) return;
    centerCameraOnBox(aggregate, camera, controls);
  }, [uploadedModels, camera, controls, scene, loadedModels, modelScale]);

  const objects: ObjectType[] = [
    { geometry: 'sphere', position: [-2, 0, -2], name: 'Sphere 1', color: '#4A9FFF' },
    { geometry: 'box', position: [2, 0, -1], name: 'Cube 1', color: '#FF4A4A' },
    { geometry: 'cylinder', position: [0, 0, 2], name: 'Cylinder 1', color: '#4CAF50' },
    { geometry: 'sphere', position: [-1, 2, 1], name: 'Sphere 2', color: '#9C27B0' },
    { geometry: 'box', position: [1, -1, 0], name: 'Cube 2', color: '#FFC107' },
    { geometry: 'cylinder', position: [2, 1, 2], name: 'Cylinder 2', color: '#E91E63' },
  ];

  // Calculate positions for uploaded models
  const getUploadedModelPositions = () => {
    if (!uploadedModels || uploadedModels.length === 0) return [];

    const positions: [number, number, number][] = [];
    for (let i = 0; i < uploadedModels.length; i++) {
      positions.push([0, 0, 0]);
    }

    return positions;
  };

  const uploadedModelPositions = getUploadedModelPositions();

  return (
    <>
      <ModelPreviewLighting />

      {/* Render uploaded models if available */}
      {uploadedModels && uploadedModels.length > 0 ? (
        // Wrap uploaded models in a single scaled group so the preview shows
        // real-world size once the user has calibrated `metersPerUnit`.
        <group scale={[modelScale, modelScale, modelScale]}>
          {uploadedModels.map((model, index) => (
            <ModelLoader
              key={`${model.name}-${index}`}
              model={model}
              position={uploadedModelPositions[index] || [0, 0, 0]}
              onHover={onObjectHover}
              onClick={onObjectSelect || onObjectClick}
              isHovered={hoveredLayer === model.name}
              onLayersFound={onLayersFound}
              visibleLayers={visibleLayers}
              isHoveredName={hoveredLayer}
              selectedLayer={selectedLayer}
              layerSettings={layerSettings}
              isLoaded={loadedModels?.[model.name]}
            />
          ))}
        </group>
      ) : (
        // Otherwise render default objects
        objects.map((obj) => (
          <InteractiveObject
            key={obj.name}
            {...obj}
            onHover={onObjectHover}
            onClick={onObjectSelect || onObjectClick}
            isHovered={hoveredLayer === obj.name}
          />
        ))
      )}
    </>
  );
}
